import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gavel, TrendingUp, Clock, Edit2, CheckCircle2, X,
  Percent, FileText, Eye, Zap, Ban, Trophy,
} from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

const STATUS_MAP = {
  draft:     { label: "Borrador",   cls: "bg-gray-100 text-gray-500"          },
  scheduled: { label: "Programada", cls: "bg-blue-100 text-blue-700"           },
  active:    { label: "En vivo 🔴", cls: "bg-green-100 text-green-700"         },
  ended:     { label: "Finalizada", cls: "bg-orange-100 text-orange-700"       },
  sold:      { label: "Vendida ✓",  cls: "bg-supernova-100 text-supernova-700" },
  cancelled: { label: "Cancelada",  cls: "bg-red-100 text-red-600"             },
};

const TABS = [
  { key: "",          label: "Todas"      },
  { key: "active",    label: "En vivo"    },
  { key: "scheduled", label: "Programadas"},
  { key: "draft",     label: "Borradores" },
  { key: "ended",     label: "Finalizadas"},
  { key: "sold",      label: "Vendidas"   },
  { key: "cancelled", label: "Canceladas" },
];

const BASE_URL = (process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, "") || "http://localhost:4000").replace(/\/$/, "");

export default function AdminAuctions() {
  const [auctions,    setAuctions]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState("");
  const [selected,    setSelected]    = useState(null); // auction being moderated
  const [modForm,     setModForm]     = useState({ platformFeePercent: "5", adminNotes: "", status: "" });
  const [saving,      setSaving]      = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}&limit=100` : "?limit=100";
      const res = await API.get(`/auctions/admin/all${params}`);
      setAuctions(res.data?.data?.auctions ?? []);
    } catch {
      toast.error("Error cargando subastas");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openModerate = (a) => {
    setSelected(a);
    setModForm({
      platformFeePercent: Number(a.platformFeePercent ?? 5).toString(),
      adminNotes:         a.adminNotes ?? "",
      status:             a.status,
    });
  };

  const handleSaveModeration = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSaving(true);
    try {
      await API.patch(`/auctions/${selected.id}/moderate`, {
        platformFeePercent: Number(modForm.platformFeePercent),
        adminNotes:         modForm.adminNotes,
        status:             modForm.status,
      });
      toast.success("Subasta moderada correctamente");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error al moderar");
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      await API.patch(`/auctions/${id}/activate`);
      toast.success("Subasta activada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error");
    }
  };

  const handleFinalize = async (id) => {
    if (!window.confirm("¿Forzar el cierre de esta subasta?")) return;
    try {
      await API.patch(`/auctions/${id}/finalize`);
      toast.success("Subasta finalizada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error");
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("¿Cancelar esta subasta?")) return;
    try {
      await API.patch(`/auctions/${id}/cancel`);
      toast.success("Subasta cancelada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error");
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-supernova-100 flex items-center justify-center">
          <Gavel size={20} className="text-supernova-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">Moderación de Subastas</h2>
          <p className="text-xs text-gray-500">Gestiona, activa y configura la comisión de Qkiere</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition whitespace-nowrap ${
              filter === t.key
                ? "bg-supernova-400 text-black"
                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <div key={i} className="bg-white rounded-2xl h-20 animate-pulse" />)}
        </div>
      ) : auctions.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Gavel size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-semibold">No hay subastas en este estado</p>
        </div>
      ) : (
        <div className="space-y-2">
          {auctions.map((a, i) => {
            const s = STATUS_MAP[a.status] ?? STATUS_MAP.draft;
            const fee = Number(a.platformFeePercent ?? 5);
            const winAmount = Number(a.currentPrice);
            const feeAmount = (winAmount * fee / 100).toFixed(0);

            return (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-4"
              >
                {/* Imagen */}
                <div className="w-14 h-14 rounded-xl bg-gray-100 flex-shrink-0 overflow-hidden">
                  {a.images?.[0]
                    ? <img src={a.images[0]?.startsWith("http") ? a.images[0] : `${BASE_URL}/${a.images[0]}`} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><Gavel size={18} className="text-gray-300" /></div>
                  }
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
                    <span className="font-semibold text-gray-800 text-sm truncate">{a.title}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                    <span className="font-semibold text-supernova-600">${Number(a.currentPrice).toLocaleString("es-CO")}</span>
                    <span className="flex items-center gap-0.5"><TrendingUp size={10} /> {a._count?.bids ?? a.totalBids} pujas</span>
                    <span className="flex items-center gap-0.5"><Clock size={10} /> {dayjs(a.endAt).format("DD/MM/YY HH:mm")}</span>
                    <span className="text-gray-500">{a.company?.name}</span>
                    <span className="flex items-center gap-0.5 text-green-600 font-semibold">
                      <Percent size={10} /> {fee}% = ${Number(feeAmount).toLocaleString("es-CO")}
                    </span>
                    {a.winner && (
                      <span className="flex items-center gap-0.5 text-yellow-600">
                        <Trophy size={10} /> {a.winner.name}
                      </span>
                    )}
                  </div>
                  {a.adminNotes && (
                    <p className="text-[10px] text-blue-600 mt-0.5 italic">📝 {a.adminNotes}</p>
                  )}
                </div>

                {/* Acciones */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Link
                    to={`/auction/${a.id}`}
                    className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-500 transition"
                    title="Ver pública"
                    target="_blank"
                  >
                    <Eye size={14} />
                  </Link>

                  {["draft", "scheduled"].includes(a.status) && (
                    <button
                      onClick={() => handleActivate(a.id)}
                      className="p-2 rounded-xl bg-green-50 hover:bg-green-100 text-green-600 transition"
                      title="Activar ahora"
                    >
                      <Zap size={14} />
                    </button>
                  )}

                  {a.status === "active" && (
                    <button
                      onClick={() => handleFinalize(a.id)}
                      className="p-2 rounded-xl bg-orange-50 hover:bg-orange-100 text-orange-600 transition"
                      title="Forzar cierre"
                    >
                      <CheckCircle2 size={14} />
                    </button>
                  )}

                  {!["sold", "cancelled", "ended"].includes(a.status) && (
                    <button
                      onClick={() => handleCancel(a.id)}
                      className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-500 transition"
                      title="Cancelar"
                    >
                      <Ban size={14} />
                    </button>
                  )}

                  <button
                    onClick={() => openModerate(a)}
                    className="p-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-600 transition"
                    title="Moderar / Comisión"
                  >
                    <Edit2 size={14} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Modal de moderación ── */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setSelected(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <Gavel size={18} className="text-supernova-500" />
                  Moderar subasta
                </h3>
                <button onClick={() => setSelected(null)} className="p-1.5 rounded-xl hover:bg-gray-100 text-gray-400">
                  <X size={17} />
                </button>
              </div>

              <form onSubmit={handleSaveModeration} className="p-5 space-y-4">
                {/* Info */}
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="font-semibold text-sm text-gray-700 truncate">{selected.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{selected.company?.name} · Precio actual: ${Number(selected.currentPrice).toLocaleString("es-CO")}</p>
                </div>

                {/* Estado */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Estado</label>
                  <select
                    value={modForm.status}
                    onChange={(e) => setModForm({ ...modForm, status: e.target.value })}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  >
                    <option value="draft">Borrador</option>
                    <option value="scheduled">Programada</option>
                    <option value="active">Activa (En vivo)</option>
                    <option value="ended">Finalizada</option>
                    <option value="sold">Vendida</option>
                    <option value="cancelled">Cancelada</option>
                  </select>
                </div>

                {/* Comisión */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1 flex items-center gap-1">
                    <Percent size={11} /> Comisión Qkiere (%)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={modForm.platformFeePercent}
                    onChange={(e) => setModForm({ ...modForm, platformFeePercent: e.target.value })}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  />
                  {modForm.platformFeePercent && (
                    <p className="text-[11px] text-green-600 mt-1 font-semibold">
                      Qkiere recibirá: ${(Number(selected.currentPrice) * Number(modForm.platformFeePercent) / 100).toLocaleString("es-CO")} sobre el precio actual
                    </p>
                  )}
                </div>

                {/* Notas admin */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1 flex items-center gap-1">
                    <FileText size={11} /> Notas internas
                  </label>
                  <textarea
                    rows={3}
                    value={modForm.adminNotes}
                    onChange={(e) => setModForm({ ...modForm, adminNotes: e.target.value })}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 resize-none"
                    placeholder="Notas visibles solo para admins..."
                  />
                </div>

                {/* Botones */}
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="flex-1 border border-gray-300 text-gray-600 font-semibold py-2.5 rounded-xl hover:bg-gray-50 transition text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-supernova-400 hover:bg-supernova-500 disabled:opacity-60 text-black font-bold py-2.5 rounded-xl transition flex items-center justify-center gap-2 text-sm"
                  >
                    {saving
                      ? <span className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full" />
                      : <><CheckCircle2 size={15} /> Guardar</>
                    }
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
